import { MIN_OD_SEGMENTS, MapStyle } from 'appConstants';
import { MergedSegmentsCollection } from 'features/map/utils/geography_utils';
import {
  addMapLayerInDefinedStack,
  getAllFeaturesFromGeoJSONSource,
  getDrawableMapSegments,
  mapLayerSafeRemove,
  mapPopRouteSegments,
  mapPushRouteSelect,
  mapSourceSafeRemove,
} from 'features/map/utils/mapCommon';
import {
  getRouteArrowBgLayerDefinition,
  getRouteArrowLayerDefinition,
  getRouteMonitorBorderLayerDefinition,
  getSelectedSegmentLayerArrowDefinition,
  getSelectedSegmentLayerDefinition,
} from 'features/map/utils/mapLayerDefinitions';
import {
  kMonitorRouteBorderLayerId,
  kRouteArrowsBgLayerId,
  kRouteArrowsLayerId,
  kSelectedSegmentLayerId,
  kSelectedSegmentSymbolsLayerId,
} from 'features/map/utils/map_constants';
import {
  Feature,
  FeatureCollection,
  LineString,
  MultiLineString,
} from 'geojson';

/** Keep off-screen segments in the OD GeoJSON source (e.g. when map is zoomed in). */
function mergeTileDrawableWithExistingSource(
  map: mapboxgl.Map,
  tileDrawable: MergedSegmentsCollection,
  segments: string[],
): MergedSegmentsCollection {
  if (!map.getSource(kSelectedSegmentLayerId)) {
    return tileDrawable;
  }

  const featureById = new Map<string, Feature>();

  for (const feature of getAllFeaturesFromGeoJSONSource(
    map,
    kSelectedSegmentLayerId,
    segments,
  )) {
    const id = feature.properties?.segment_id;
    if (id) featureById.set(id, feature);
  }

  const segmentSet = new Set(segments);
  //add new and update old data with new info
  for (const feature of tileDrawable.features ?? []) {
    const id = feature.properties?.segment_id;
    if (id && segmentSet.has(id)) {
      featureById.set(id, feature);
    }
  }

  const features = Array.from(featureById.values()) as Feature<LineString>[];

  return {
    type: 'FeatureCollection',
    features,
    properties: {
      segment_ids: features.map((f) => f.properties?.segment_id),
    },
  };
}

async function fetchAndMergeMissingSegments(
  drawable: MergedSegmentsCollection | undefined,
  segments: string[],
  fetchMissingSegments: (
    missingIds: string[],
  ) => Promise<Feature<LineString>[]>,
): Promise<MergedSegmentsCollection> {
  const existingIds = drawable ? drawable.properties.segment_ids : [];

  const segsMissing = existingIds.length < segments.length;
  if (!segsMissing) return drawable;

  const missingSegs = segments.filter((x) => !existingIds.includes(x));
  if (missingSegs.length === 0) return drawable;
  const missingFeatures = await fetchMissingSegments(missingSegs);
  if (!missingFeatures?.length) return drawable;
  return {
    type: 'FeatureCollection',
    ...(drawable ?? {}),
    properties: {
      ...(drawable?.properties ?? {}),
      segment_ids: [...existingIds, ...missingSegs],
    },
    features: [...(drawable?.features ?? []), ...missingFeatures],
  };
}

function applyHoveredIds(
  data: MergedSegmentsCollection,
  hoveredIds: string[] | null | undefined,
): MergedSegmentsCollection {
  if (!data?.features) return data;

  const getRole = (f: Feature) => {
    if (hoveredIds?.length === MIN_OD_SEGMENTS) {
      if (hoveredIds[0] === f.properties?.segment_id) return 'start';
      else if (hoveredIds[1] === f.properties?.segment_id) return 'end';
    }
    return 'none';
  };
  return {
    ...data,
    features: data.features.map((f) => {
      return {
        type: f.type,
        geometry: f.geometry,
        properties: {
          ...(f.properties || {}),
          ...f.properties,
          isHovered: hoveredIds
            ? hoveredIds.includes(f.properties?.segment_id)
            : false,
          role: getRole(f),
        },
      };
    }),
  };
}

export function getActiveMatrixOdPair(
  hoveredSegmentIds: string[] | null,
  clickedMatrixSegmentIds: string[] | null,
): [string, string] | null {
  if (hoveredSegmentIds?.length === MIN_OD_SEGMENTS)
    return [hoveredSegmentIds[0], hoveredSegmentIds[1]];
  if (clickedMatrixSegmentIds?.length === MIN_OD_SEGMENTS)
    return [clickedMatrixSegmentIds[0], clickedMatrixSegmentIds[1]];
  return null;
}

export function getMtrGeojsonForOdPair(
  geojson: FeatureCollection<MultiLineString> | undefined,
  originId: string,
  destId: string,
): FeatureCollection<MultiLineString> | undefined {
  if (!geojson?.features?.length) return undefined;

  const features = geojson.features.filter(
    (feature) =>
      feature.properties?.origin_tt_seg_id === originId &&
      feature.properties?.dest_tt_seg_id === destId,
  );
  if (!features.length) return undefined;

  return { type: 'FeatureCollection', features };
}

export function clearMtrLayer(map: mapboxgl.Map) {
  mapPopRouteSegments(map);
}

export function redrawMtrLayer(
  map: mapboxgl.Map,
  mapStyle: MapStyle,
  geojson: FeatureCollection<MultiLineString> | undefined,
) {
  clearMtrLayer(map);
  if (!geojson?.features?.length) return;
  mapPushRouteSelect(geojson, map, mapStyle);
}

/** Update hover styling on the existing source without a geometry redraw. */
export function updateODSegmentHover(
  map: mapboxgl.Map,
  hoveredIds: string[] | null | undefined,
) {
  const source = map.getSource(
    kSelectedSegmentLayerId,
  ) as mapboxgl.GeoJSONSource & { _data?: MergedSegmentsCollection };
  const data = source?._data;
  if (!source || !data?.features?.length) return;
  source.setData(applyHoveredIds(data, hoveredIds));
}

export async function redrawSelectedODSegments(
  map: mapboxgl.Map,
  mapStyle: MapStyle,
  segments: string[] | undefined,
  force = false,
  fetchMissingSegments?: (
    missingIds: string[],
  ) => Promise<Feature<LineString>[]>,
  hoveredIds?: string[] | null,
  includeMissingSegments = true,
) {
  if (segments?.length > 0) {
    const { needsRedraw, drawable } = getDrawableMapSegments(
      map,
      segments,
      kSelectedSegmentLayerId,
      force,
    );
    const shouldRedrawSegments = needsRedraw && drawable;
    let finalDrawable: MergedSegmentsCollection;
    if (shouldRedrawSegments) {
      finalDrawable = mergeTileDrawableWithExistingSource(
        map,
        drawable,
        segments,
      );
    }
    if (fetchMissingSegments && includeMissingSegments) {
      finalDrawable = await fetchAndMergeMissingSegments(
        finalDrawable,
        segments,
        fetchMissingSegments,
      );
    }
    if (finalDrawable) {
      finalDrawable = applyHoveredIds(finalDrawable, hoveredIds);

      const source = map.getSource(
        kSelectedSegmentLayerId,
      ) as mapboxgl.GeoJSONSource;
      if (!source) {
        map.addSource(kSelectedSegmentLayerId, {
          type: 'geojson',
          data: finalDrawable,
        });
        addMapLayerInDefinedStack(
          map,
          getSelectedSegmentLayerDefinition(mapStyle),
        );
        addMapLayerInDefinedStack(
          map,
          getSelectedSegmentLayerArrowDefinition(mapStyle),
        );
        addMapLayerInDefinedStack(map, {
          ...getRouteMonitorBorderLayerDefinition(mapStyle),
          source: kSelectedSegmentLayerId,
        });
        addMapLayerInDefinedStack(map, {
          ...getRouteArrowLayerDefinition(mapStyle),
          source: kSelectedSegmentLayerId,
        });
        addMapLayerInDefinedStack(map, {
          ...getRouteArrowBgLayerDefinition(mapStyle),
          source: kSelectedSegmentLayerId,
        });
        return;
      }
      source.setData(finalDrawable);
    } else if (hoveredIds !== undefined) {
      updateODSegmentHover(map, hoveredIds);
    }
  } else {
    mapLayerSafeRemove(map, kSelectedSegmentLayerId);
    mapLayerSafeRemove(map, kSelectedSegmentSymbolsLayerId);
    mapLayerSafeRemove(map, kMonitorRouteBorderLayerId);
    mapLayerSafeRemove(map, kRouteArrowsLayerId);
    mapLayerSafeRemove(map, kRouteArrowsBgLayerId);
    mapSourceSafeRemove(map, kSelectedSegmentLayerId);
  }
}
